import { useState, useEffect, useRef, useCallback } from "react"
import { GetHelpCenterTOC, GetArticle, GetArticleInCategory } from "../api"
import { usePromise } from "aldoo-ra/usePromise"
import { getHelpCenterBase } from "../urlUtils"
import Value from "aldoo-ra/Value"
import TextBundle from "aldoo-ra/CMS/text-bundle"

function useContentLoader() {
  const texts = TextBundle({ id: "help-center" })

  const [currentLanguage] = Value({
    key: "current-site-language",
    defaultValue: "en",
    persistKey: "current-site-language",
  })

  const lastLoadedLanguage = useRef(currentLanguage)

  const [selectedContent, setSelectedContent] = useState(null)
  const [contentType, setContentType] = useState(null)
  const [error, setError] = useState(null)
  const [rootCategories, setRootCategories] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({})
  const [activeContentSlug, setActiveContentSlug] = useState(null)
  const [activeContentType, setActiveContentType] = useState(null)
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: texts?.help_center || "Help Center", link: getHelpCenterBase() },
  ])
  const [browserInitialized, setBrowserInitialized] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const initRef = useRef(false)
  const refreshPendingRef = useRef(false)
  const loadContentFromUrlRef = useRef(null)

  const activeContentRef = useRef({ type: null, slug: null, categoryPath: null })

  const getTocPromise = useCallback(
    async (language = currentLanguage) => {
      const response = await GetHelpCenterTOC(language)
      if (response && response.success && response.toc) return response.toc
      throw new Error(texts?.toc_load_error || "Failed to load help center structure")
    },
    [currentLanguage]
  )

  const {
    result: tocData,
    pending: isTocLoading,
    error: tocError,
    execute: executeTocLoad,
    refresh: refreshToc,
  } = usePromise(getTocPromise, true)

  const isLoading = Boolean(isTocLoading)
  const combinedError = error || (tocError instanceof Error ? tocError.message : tocError) || null

  const parseUrlPath = useCallback(() => {
    if (typeof window === "undefined")
      return { type: null, categoryPath: null, articleSlug: null }

    const path = window.location.pathname
    const hcIndex = path.indexOf("/help-center")

    if (hcIndex === -1)
      return { type: null, categoryPath: null, articleSlug: null }

    if (path.includes("/help-center/submit-request"))
      return { type: null, categoryPath: null, articleSlug: null }

    const relativePath = path.substring(hcIndex + "/help-center".length)

    if (!relativePath || relativePath === "/") {
      setExpandedCategories({})
      return { type: null, categoryPath: null, articleSlug: null }
    }

    const segments = relativePath.split("/").filter(Boolean)

    if (
      segments.length >= 3 &&
      segments[0] === "category" &&
      segments.includes("article")
    ) {
      const articleIndex = segments.indexOf("article")
      return {
        type: "article",
        categoryPath: segments.slice(1, articleIndex).join("/"),
        articleSlug: segments[articleIndex + 1],
      }
    } else if (segments.length >= 2 && segments[0] === "article") {
      return { type: "article", categoryPath: null, articleSlug: segments[1] }
    } else if (segments.length >= 2 && segments[0] === "category") {
      return {
        type: "category",
        categoryPath: segments.slice(1).join("/"),
        articleSlug: null,
      }
    }

    return { type: null, categoryPath: null, articleSlug: null }
  }, [setExpandedCategories])

  const findArticleInToc = useCallback(
    (slug, categoryPath = null) => {
      if (!tocData || !tocData.content || !tocData.content.categories)
        return null

      let categories = tocData.content.categories
      let breadcrumbData = []
      let foundArticle = null

      if (categoryPath) {
        const pathParts = categoryPath.split("/")
        let currentPath = ""

        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i]
          currentPath = i === 0 ? part : `${currentPath}/${part}`
          const catLabel =
            part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
          const previousPath = i > 0 ? pathParts.slice(0, i).join("/") : ""
          breadcrumbData.push({
            label: catLabel,
            link: `/help-center/category/${currentPath}`,
            categorySlug: part,
            parentPath: previousPath,
          })
        }

        let currentCategories = categories
        for (const part of pathParts) {
          const nextCategory = currentCategories.find((c) => c.slug === part)
          if (!nextCategory) return null

          if (nextCategory.articles) {
            foundArticle = nextCategory.articles.find((a) => a.slug === slug)
            if (foundArticle) break
          }

          if (!nextCategory.categories) break
          currentCategories = nextCategory.categories
        }

        if (!foundArticle && currentCategories) {
          for (const cat of currentCategories) {
            if (cat.articles) {
              foundArticle = cat.articles.find((a) => a.slug === slug)
              if (foundArticle) break
            }
          }
        }
      } else {
        const searchInCategories = (categories, path = []) => {
          for (const cat of categories) {
            const currentPath = [...path, cat]

            if (cat.articles) {
              const article = cat.articles.find((a) => a.slug === slug)
              if (article) {
                breadcrumbData = currentPath.map((category, index) => {
                  const pathSoFar = currentPath
                    .slice(0, index + 1)
                    .map((c) => c.slug)
                    .join("/")
                  return {
                    label: category.label,
                    link: `/help-center/category/${pathSoFar}`,
                    categorySlug: category.slug,
                    parentPath:
                      index > 0
                        ? currentPath
                            .slice(0, index)
                            .map((c) => c.slug)
                            .join("/")
                        : "",
                  }
                })
                return { article, breadcrumbData }
              }
            }

            if (cat.categories && cat.categories.length > 0) {
              const result = searchInCategories(cat.categories, currentPath)
              if (result) return result
            }
          }
          return null
        }

        const result = searchInCategories(categories)
        if (result) {
          foundArticle = result.article
          breadcrumbData = result.breadcrumbData
        }
      }

      if (!foundArticle) return null
      return { articleMetadata: foundArticle, breadcrumbData }
    },
    [tocData]
  )

  const findCategoryInToc = useCallback(
    (categoryPath) => {
      if (!tocData || !tocData.content || !tocData.content.categories || !categoryPath)
        return null

      // Try exact path traversal from root
      const tryExactPath = (categories, parts) => {
        let currentCategories = categories
        let currentCategory = null
        let breadcrumbData = []
        let currentPath = ""

        for (let i = 0; i < parts.length; i++) {
          const part = parts[i]
          currentPath = i === 0 ? part : `${currentPath}/${part}`
          currentCategory = currentCategories.find((c) => c.slug === part)
          if (!currentCategory) return null

          const catLabel = currentCategory.label ||
            part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
          const previousPath = i > 0 ? parts.slice(0, i).join("/") : ""

          if (i === parts.length - 1) {
            breadcrumbData.push({ label: catLabel })
          } else {
            breadcrumbData.push({
              label: catLabel,
              link: `/help-center/category/${currentPath}`,
              categorySlug: part,
              parentPath: previousPath,
            })
          }

          if (currentCategory.categories) {
            currentCategories = currentCategory.categories
          } else if (i < parts.length - 1) {
            return null
          }
        }

        if (!currentCategory) return null
        return { currentCategory, breadcrumbData }
      }

      // Recursive search by last slug segment anywhere in the tree
      const findBySlug = (categories, slug, ancestorBreadcrumbs = []) => {
        for (const cat of categories) {
          if (cat.slug === slug) {
            return {
              currentCategory: cat,
              breadcrumbData: [...ancestorBreadcrumbs, { label: cat.label || slug }],
            }
          }
          if (cat.categories?.length) {
            const found = findBySlug(cat.categories, slug, [
              ...ancestorBreadcrumbs,
              {
                label: cat.label || cat.slug,
                link: `/help-center/category/${cat.slug}`,
                categorySlug: cat.slug,
                parentPath: "",
              },
            ])
            if (found) return found
          }
        }
        return null
      }

      const pathParts = categoryPath.split("/")
      let found = tryExactPath(tocData.content.categories, pathParts)

      // Fall back to searching by the last path segment
      if (!found) {
        const targetSlug = pathParts[pathParts.length - 1]
        found = findBySlug(tocData.content.categories, targetSlug)
      }

      if (!found) return null

      const { currentCategory, breadcrumbData } = found
      const categoryContent = {
        content: {
          type: "HCCategory",
          name: currentCategory.label,
          slug: currentCategory.slug,
          categories: currentCategory.categories || [],
          articles: currentCategory.articles || [],
          notes: currentCategory.notes || [],
          icon: currentCategory.icon,
        },
        _id: currentCategory._id,
        path: categoryPath,
        breadcrumbData,
      }

      return { categoryContent, breadcrumbData }
    },
    [tocData]
  )

  const buildExpansionMap = useCallback(
    (categoryPath) => {
      if (!categoryPath) return {}

      const pathParts = categoryPath.split("/")
      const newExpandedCategories = { ...expandedCategories }
      let cumulativePath = ""

      for (const slug of pathParts) {
        cumulativePath = cumulativePath ? `${cumulativePath}/${slug}` : slug
        newExpandedCategories[cumulativePath] = true
        newExpandedCategories[slug] = true
      }

      return newExpandedCategories
    },
    [expandedCategories]
  )

  const loadArticle = useCallback(
    async (slug, categoryPath = null) => {
      if (!slug || !tocData) return null

      setError(null)

      try {
        activeContentRef.current = { type: "article", slug, categoryPath }

        // Try exact path first, fall back to recursive tree search
        let result = findArticleInToc(slug, categoryPath)
        if (!result) result = findArticleInToc(slug, null)

        // Derive the full tree path from TOC breadcrumbs for sidebar expansion
        const fullCategoryPath = result?.breadcrumbData
          ?.filter((b) => b.categorySlug)
          .map((b) => b.categorySlug)
          .join("/") || categoryPath

        if (fullCategoryPath) {
          setExpandedCategories(buildExpansionMap(fullCategoryPath))

          // If the derived path differs from the provided one, update the URL so the sidebar can match
          if (fullCategoryPath !== categoryPath && typeof window !== "undefined") {
            const newUrl = `${getHelpCenterBase()}/category/${fullCategoryPath}/article/${slug}`
            if (window.location.pathname !== newUrl) {
              window.skipNextHelpCenterUrlChange = true
              window.history.replaceState(null, "", newUrl)
            }
          }
        }

        const articleData = fullCategoryPath
          ? await GetArticleInCategory(fullCategoryPath, slug)
          : await GetArticle(slug)

        if (!articleData) throw new Error(`Article not found: ${slug}`)

        // Build breadcrumbs from TOC result or fall back to URL path
        let breadcrumbData = result?.breadcrumbData || []
        if (!breadcrumbData.length && categoryPath) {
          breadcrumbData = categoryPath.split("/").map((part, i, arr) => ({
            label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " "),
            link: i < arr.length - 1 ? `/help-center/category/${arr.slice(0, i + 1).join("/")}` : undefined,
          })).filter(b => b.link)
        }

        const newBreadcrumbs = [
          { label: texts?.help_center || "Help Center", link: getHelpCenterBase() },
          ...breadcrumbData,
        ]

        if (
          newBreadcrumbs[newBreadcrumbs.length - 1].label !==
          articleData.content.name
        ) {
          newBreadcrumbs.push({ label: articleData.content.name })
        }

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(articleData)
        setContentType("article")
        setActiveContentSlug(slug)
        setActiveContentType("article")
        lastLoadedLanguage.current = currentLanguage

        return articleData
      } catch (err) {
        console.error("loadArticle error:", err)
        setError(texts?.article_load_error || "Failed to load article. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findArticleInToc, tocData]
  )

  const loadCategory = useCallback(
    async (categoryPath) => {
      if (!categoryPath || !tocData) return null

      setError(null)

      try {
        activeContentRef.current = { type: "category", slug: categoryPath, categoryPath: null }

        if (categoryPath.includes("/")) {
          setExpandedCategories(buildExpansionMap(categoryPath))
        }

        const result = findCategoryInToc(categoryPath)
        if (!result) throw new Error(`Category not found: ${categoryPath}`)

        const { categoryContent, breadcrumbData } = result

        const newBreadcrumbs = [
          { label: texts?.help_center || "Help Center", link: getHelpCenterBase() },
          ...breadcrumbData,
        ]

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(categoryContent)
        setContentType("category")
        setActiveContentSlug(categoryPath)
        setActiveContentType("category")
        lastLoadedLanguage.current = currentLanguage

        return categoryContent
      } catch (err) {
        setError(texts?.category_load_error || "Failed to load category. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findCategoryInToc, tocData]
  )

  const loadContentFromUrl = useCallback(async () => {
    if (!tocData) return

    setError(null)

    try {
      const urlParams = parseUrlPath()

      if (!urlParams.type && !urlParams.categoryPath && !urlParams.articleSlug) {
        setExpandedCategories({})
        setSelectedContent(null)
        setContentType(null)
        setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
        setActiveContentSlug(null)
        setActiveContentType(null)
        activeContentRef.current = { type: null, slug: null, categoryPath: null }
      } else if (urlParams.categoryPath) {
        setExpandedCategories(buildExpansionMap(urlParams.categoryPath))
      }

      if (urlParams.type === "article") {
        await loadArticle(urlParams.articleSlug, urlParams.categoryPath)
      } else if (urlParams.type === "category") {
        await loadCategory(urlParams.categoryPath)
      }

      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError(texts?.content_load_error || "Failed to load content. Please try again later.")
    }
  }, [
    parseUrlPath,
    buildExpansionMap,
    loadArticle,
    loadCategory,
    currentLanguage,
    tocData,
  ])

  const refreshContent = useCallback(async () => {
    setError(null)

    try {
      const currentPath = window.location.pathname
      const isHomePage = /\/help-center\/?$/.test(currentPath)

      if (isHomePage) {
        setExpandedCategories({})
        setActiveContentSlug(null)
        setActiveContentType(null)
        setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
        setSelectedContent(null)
        setContentType(null)
        activeContentRef.current = { type: null, slug: null, categoryPath: null }
      } else {
        refreshPendingRef.current = true
      }

      const loadMethod = refreshToc || executeTocLoad || GetHelpCenterTOC
      loadMethod(currentLanguage)
      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError(`${texts?.refresh_error_prefix || "Failed to refresh content"}: ${err.message}`)
    }
  }, [currentLanguage, refreshToc, executeTocLoad])

  const updateUrl = useCallback((type, slug, categoryPath = "") => {
    if ((type === "article" || type === "category") && !slug) return

    let url = getHelpCenterBase()

    if (type === "article") {
      url += categoryPath
        ? `/category/${categoryPath}/article/${slug}`
        : `/article/${slug}`
    } else if (type === "category") {
      url += `/category/${categoryPath ? `${categoryPath}/` : ""}${slug}`
    }

    if (window.location.pathname !== url) {
      window.skipNextHelpCenterUrlChange = true
      window.history.pushState({ type, slug, categoryPath }, "", url)
      window.dispatchEvent(new CustomEvent("helpCenterNavigate"))
    }
  }, [])

  const expandCategoryPath = useCallback(
    (path) => {
      if (!path) return
      setExpandedCategories(
        buildExpansionMap(typeof path === "string" ? path : path.join("/"))
      )
    },
    [buildExpansionMap]
  )

  const loadToc = useCallback(() => {
    executeTocLoad(currentLanguage)
  }, [executeTocLoad, currentLanguage])

  // Hydration
  useEffect(() => { setIsHydrated(true) }, [])

  // Initial TOC load
  useEffect(() => {
    if (!tocData && !isTocLoading) executeTocLoad(currentLanguage)
  }, [])

  // Home page state init
  useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return
    const path = window.location.pathname
    const isHelpCenterHome = /\/help-center\/?$/.test(path)

    if (isHelpCenterHome && !selectedContent && !contentType) {
      setSelectedContent(null)
      setContentType(null)
      setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
      setActiveContentSlug(null)
      setActiveContentType(null)
      setExpandedCategories({})
      activeContentRef.current = { type: null, slug: null, categoryPath: null }
    }
  }, [isHydrated, selectedContent, contentType])

  // Sync root categories from TOC
  useEffect(() => {
    if (tocData && tocData.content && tocData.content.categories) {
      setRootCategories(tocData.content.categories)
    }
  }, [tocData])

  // Keep ref current so the tocData effect always calls the latest version
  // without needing loadContentFromUrl in its dependency array
  useEffect(() => {
    loadContentFromUrlRef.current = loadContentFromUrl
  }, [loadContentFromUrl])

  // Initialize content from URL once TOC loads; also reload after language refresh.
  // Depends only on tocData so it fires only when new TOC data arrives,
  // not when loadContentFromUrl is recreated due to a currentLanguage change
  // (which would prematurely consume refreshPendingRef before the new TOC is ready).
  useEffect(() => {
    if (!tocData) return

    if (!initRef.current) {
      initRef.current = true
      const currentUrl = typeof window !== "undefined" ? window.location.pathname : ""
      if (currentUrl === "/help-center/submit-request") {
        setBrowserInitialized(true)
        return
      }
      loadContentFromUrlRef.current().then(() => setBrowserInitialized(true))
      return
    }

    if (refreshPendingRef.current) {
      refreshPendingRef.current = false
      const path = typeof window !== "undefined" ? window.location.pathname : ""
      if (!path.includes("/submit-request")) loadContentFromUrlRef.current()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tocData])

  // URL change monitor
  useEffect(() => {
    if (!isHydrated || !tocData) return

    let lastUrl = window.location.pathname
    let isProcessingUrlChange = false
    let debounceTimer = null

    const handleUrlChange = async () => {
      if (isProcessingUrlChange) return
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      if (window.skipNextHelpCenterUrlChange) {
        window.skipNextHelpCenterUrlChange = false
        return
      }

      const currentUrl = window.location.pathname
      if (
        !currentUrl.includes("/help-center") ||
        currentUrl.includes("/help-center/submit-request")
      ) return

      isProcessingUrlChange = true

      try {
        const shouldBeHomePage = /\/help-center\/?$/.test(currentUrl)

        if (shouldBeHomePage) {
          setSelectedContent(null)
          setContentType(null)
          setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
          setActiveContentSlug(null)
          setActiveContentType(null)
          setExpandedCategories({})
          activeContentRef.current = { type: null, slug: null, categoryPath: null }
        } else {
          await loadContentFromUrl()
        }
      } catch (error) {
        setError(texts?.url_content_load_error || "Failed to load content")
      } finally {
        isProcessingUrlChange = false
      }
    }

    const urlMonitor = setInterval(() => {
      const currentUrl = window.location.pathname
      if (
        currentUrl.includes("/help-center") &&
        !currentUrl.includes("/help-center/submit-request") &&
        lastUrl !== currentUrl
      ) {
        lastUrl = currentUrl
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(handleUrlChange, 100)
      }
    }, 1000)

    const handlePopState = () => {
      const currentUrl = window.location.pathname
      if (
        currentUrl.includes("/help-center") &&
        !currentUrl.includes("/help-center/submit-request")
      ) {
        lastUrl = currentUrl
        setTimeout(handleUrlChange, 50)
      }
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      clearInterval(urlMonitor)
      if (debounceTimer) clearTimeout(debounceTimer)
      window.removeEventListener("popstate", handlePopState)
    }
  }, [isHydrated, tocData, loadContentFromUrl])

  return {
    selectedContent,
    contentType,
    isLoading,
    error: combinedError,
    tocData,
    rootCategories,
    expandedCategories,
    activeContentSlug,
    activeContentType,
    breadcrumbItems,
    browserInitialized,
    categoriesLoaded: !!tocData,
    currentLanguage,
    setExpandedCategories,
    setActiveContentSlug,
    setActiveContentType,
    setBreadcrumbItems,
    setSelectedContent,
    setContentType,
    setError,
    loadArticle,
    loadCategory,
    parseUrlPath,
    buildExpansionMap,
    updateUrl,
    loadContentFromUrl,
    expandCategoryPath,
    loadToc,
    refreshContent,
  }
}

export default useContentLoader
